<template>
  <div>
    <h1 class="font-weight-bold mb-2">Customer Management</h1>
    <!-- Statistics -->
    <div v-if="loadingUserStatistics" class="text-center mb-1">
      <Spinner variant="primary" size="medium" />
    </div>
    <CustomerStatisticsStats v-else :statistics="statistics" />
    <!-- Filters -->
    <CustomerListFilters
      :register-date.sync="registerDate"
      :last-logged-date.sync="lastLoggedDate"
      :bdm-order.sync="bdmOrderValue"
      :tag-order.sync="tagOrderValue"
      :profile-order.sync="profileOrderValue"
      :status-order.sync="statusOrderValue"
      :bdm-options="bdmOptions"
      :tag-options="tagOptions"
      :profile-options="profileOptions"
      :status-options="statusOptions"
      @filter="filter"
      @clear="clearFilter"
      @export="handleExportData"
      :exportData="exportData"
      @data-exported="resetExportData"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>customers</label>
          </b-col>
          <!-- Search -->
          <b-col cols="12" md="6" class="d-flex justify-content-end">
            <div class="d-flex align-items-center justify-content-end w-100">
              <b-form-input
                v-model="search"
                class="d-inline-block"
                placeholder="Search..."
              >
              </b-form-input>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        :key="tableKey"
        :items="fetchUsers"
        :fields="tableColumns"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        responsive
        primary-key="userId"
        thead-class="text-nowrap"
      >
        <template #empty>
          <div v-if="loadingUsers" class="text-center text-dark my-2">
            <p>Loading... Please wait</p>
          </div>

          <div
            v-else-if="!loadingUsers && users.length === 0"
            class="text-center text-dark my-2"
          >
            No matching records found
          </div>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="{ item }">
          <b-link
            :to="{ name: 'customers-view', params: { id: item.userId } }"
            class="text-nowrap"
          >
            {{ item.firstName }} {{ item.middleName }} {{ item.lastName }}
          </b-link>
        </template>

        <!-- Column: Email/Phone -->
        <template #cell(emailMobile)="{ item }">
          <b-media vertical-align="center">
            <div class="d-flex align-items-center">{{ item.email }}</div>
            <div class="d-flex align-items-center">{{ item.phoneNumber }}</div>
          </b-media>
        </template>

        <!-- Column: BDM -->
        <template #cell(bdm)="{ item }">
          <b-media vertical-align="center" v-if="item.primaryAffiliateName">
            <span class="text-nowrap">{{ item.primaryAffiliateName }}</span>
          </b-media>
          <span v-else>-</span>
        </template>

        <!-- Column: Profile -->
        <template #cell(profile)="{ item }">
          <b-media vertical-align="center" v-if="item.profile">
            <div class="d-flex align-items-center">{{ item.profile }}</div>
          </b-media>
          <span v-else>-</span>
        </template>

        <!-- Column: DOB / State -->
        <template #cell(dobState)="{ item }">
          <b-media vertical-align="center">
            <div class="d-flex align-items-center">
              {{ formatDobState(item) }}
            </div>
          </b-media>
        </template>

        <!-- Column: balance -->
        <template #cell(balance)="{ item }">
          <b-media vertical-align="center">
            <div class="d-flex align-items-center">
              {{ formatCurrencyBuiltInFunc(item.balance) }}
            </div>
          </b-media>
        </template>

        <!-- Column: Date of Rego -->
        <template #cell(date)="{ item }">
          <b-media vertical-align="center">
            <span class="d-flex align-items-center">
              {{ format(new Date(item.createdTime), "dd/MM/yyyy") }}
            </span>
            <span class="d-flex align-items-center small">
              {{ format(new Date(item.createdTime), "hh:mm:ss") }}
            </span>
          </b-media>
        </template>
        <template #cell(totalGgr)="{ item }">
          <b-media vertical-align="center">
            <div class="d-flex flex-column">
              <span class="d-flex align-items-center">
                {{ formatCurrencyBuiltInFunc(item.totalGgr) }}
              </span>
              <span class="d-flex align-items-center small text-muted">
                {{ formatPercent(item.ggrPercentage) }}
              </span>
            </div>
          </b-media>
        </template>

        <!-- Column: GGR% (break status + end date) -->
        <template #cell(ggrBreak)="{ item }">
          <b-media vertical-align="center">
            <div
              v-if="
                item.breakStartDate &&
                item.breakTime &&
                new Date(item.breakTime) > new Date()
              "
            >
              <div class="d-flex align-items-center">
                {{ getBreakType(item) }}
              </div>
              <div class="d-flex align-items-center small text-muted">
                Ends: {{ format(new Date(item.breakTime), "dd/MM/yyyy") }}
              </div>
            </div>
            <span v-else>-</span>
          </b-media>
        </template>

        <!-- Column: Limits -->
        <template #cell(limit)="{ item }">
          <b-media vertical-align="center">
            <span v-if="item.depositLimits.length === 0">-</span>
            <div
              class="d-flex flex-column align-items-center"
              v-else
              v-for="(limit, idx) in item.depositLimits"
              :key="idx"
            >
              <span class="text-nowrap"
                >{{ formatCurrencyBuiltInFunc(limit.amount) }}/{{
                  formatPeriod(limit.period)
                }}</span
              >
            </div>
          </b-media>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="{ item }">
          <b-media vertical-align="center">
            <b-badge pill :variant="variantStatus(item)">
              {{ formatStatus(item) }}
            </b-badge>
          </b-media>
        </template>

        <!-- Column: Last Bet -->
        <template #cell(lastBet)="{ item }">
          <b-media vertical-align="center" v-if="item.lastBetslipCreatedTime">
            <span class="d-flex align-items-center">
              {{ format(new Date(item.lastBetslipCreatedTime), "dd/MM/yyyy") }}
            </span>
            <span class="d-flex align-items-center small">
              {{ format(new Date(item.lastBetslipCreatedTime), "hh:mm:ss") }}
            </span>
          </b-media>
          <span v-else>-</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="{ item }">
          <b-media vertical-align="center">
            <div class="d-flex align-items-center">
              <feather-icon
                size="20"
                icon="EyeIcon"
                class="pointer text-primary mr-1"
                @click="showModal('delete')"
              />
              <b-link
                :to="{ name: 'customers-edit', params: { id: item.userId } }"
              >
                <feather-icon size="20" icon="Edit2Icon" class="pointer mr-1" />
              </b-link>
              <feather-icon
                size="20"
                icon="TagIcon"
                class="pointer text-muted"
              />
            </div>
          </b-media>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Showing {{ metadata.from }} to {{ metadata.to }} of
              {{ metadata.of }} customers
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="metadata.of"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-modal
      ref="modal"
      :title="modal.title"
      centered
      cancel-variant="outline-secondary"
      :ok-disabled="isSubmitting"
      @ok="handleSubmitModal"
      hide-footer
    >
      <b-container fluid>
        <b-row>
          <b-col cols="12"><p class="text-body mb-0">IP Address</p></b-col>
          <b-col cols="12"
            ><p class="text-body font-weight-bolder">192.168.1.100</p></b-col
          >
        </b-row>

        <b-row>
          <b-col cols="12"><p class="text-body mb-0">User Agent</p></b-col>
          <b-col cols="12"
            ><p class="text-body">
              Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36
            </p></b-col
          >
        </b-row>

        <b-row>
          <b-col cols="12" class="d-flex align-items-center" style="gap: 8px">
            <p class="text-body mb-0">BOT Status</p>
            <div
              class="rounded bg-success d-flex align-items-center justify-content-center"
              style="padding: 2px"
            >
              <p class="text-white font-weight-bold mb-0">NO</p>
            </div></b-col
          >
        </b-row>
        <b-row class="mt-1">
          <b-col class="d-flex align-items-center" style="gap: 8px">
            <b-button size="sm" variant="outline-danger">Bet stopped</b-button>
            <b-button size="sm" variant="outline-success"
              >Check IP Location</b-button
            >
            <b-button size="sm" variant="outline-primary"
              >Check Duplicate IPs</b-button
            >
          </b-col>
        </b-row>
      </b-container>
      <template #modal-ok>
        <Spinner v-if="isSubmitting" />
        <span v-else>Submit</span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Spinner from "@core/components/Spinner";
import { UserAction, UserClosed } from "@core/utils/enum/user";
import {
  BBadge,
  BButton,
  BCard,
  BCardText,
  BCol,
  BContainer,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BFormCheckbox,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  VBModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapState } from "vuex";
// import ExportCSV from './ExportCSV.vue'
import {
  formatCurrencyBuiltInFunc,
  formatDateTimeAustralia,
} from "@/helpers/utils";
import {
  format,
  startOfDay,
  endOfDay,
  differenceInDays,
  parseISO,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";
import { debounce } from "debounce";
import CustomerListFilters from "./CustomerListFilters";
import CustomerStatisticsStats from "./CustomerStatisticsStats.vue";

export default {
  components: {
    CustomerStatisticsStats,
    CustomerListFilters,
    BButton,
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    BTable,
    BMedia,
    BLink,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    Spinner,
    BBadge,
    BContainer,
  },
  directives: {
    "b-modal": VBModal,
  },
  data() {
    const bdmOptions = [{ label: "All", value: null }];
    const tagOptions = [{ label: "All", value: null }];
    const profileOptions = [{ label: "All", value: null }];
    const statusOptions = [
      { label: "All", value: null },
      { label: "Active", value: "Active" },
      { label: "Inactive", value: "Inactive" },
      { label: "UnVerified", value: "UnVerified" },
      { label: "Blocked", value: "Blocked" },
      { label: "Bet Stopped", value: "Bet Stopped" },
      { label: "Closed", value: "Closed" },
      { label: "Break", value: "Break" },
      { label: "Self Excluded", value: "Self Excluded" },
      { label: "Banned", value: "Ind. Banned" },
    ];
    return {
      UserClosed,
      action: {
        type: "",
        id: "",
        status: "",
      },
      modal: {
        title: "",
        content: "",
      },
      UserAction,
      formatDateTimeAustralia,
      exportData: [],
      metadata: {
        from: 0,
        to: 0,
        of: 0,
      },
      tableColumns: [
        { key: "name", label: "Name" },
        { key: "status", label: "Status" },
        { key: "dobState", label: "DOB / State" },
        { key: "emailMobile", label: "Email / Mobile" },
        { key: "bdm", label: "BDM" },
        { key: "profile", label: "Profile" },
        // { key: "balance", label: "Balance" },
        { key: "date", label: "Date of Rego" },
        { key: "totalGgr", label: "Total GGR" },
        { key: "ggrBreak", label: "GGR%" },
        { key: "limit", label: "Limits" },
        { key: "lastBet", label: "Last Bet" },
        { key: "actions" },
      ],
      tableKey: 0,
      perPage: 100,
      currentPage: 1,
      perPageOptions: [10, 25, 50, 100],
      search: null,
      roleFilter: null,
      registerDate: "",
      lastLoggedDate: "",
      bdmOrderValue: bdmOptions[0],
      tagOrderValue: tagOptions[0],
      profileOrderValue: profileOptions[0],
      statusOrderValue: statusOptions[0],
      isSortDirDesc: false,
      sortBy: null,
      profileOptions,
      bdmOptions,
      tagOptions,
      statusOptions,
      sortOrderValue: "DESC",
      sortFieldValue: "createdTime",

      exportParams: {},
      exportData: [],
    };
  },
  computed: {
    ...mapState("auth", ["permissions"]),
    ...mapState({
      verifyAccount: (s) => s.user.verifyAccount,
      list: (s) => s.user.list,
      verify: (s) => s.user.verify,
      block: (s) => s.user.block,
      delete: (s) => s.user.remove,
      reopen: (s) => s.user.reopenAccount,
    }),
    ...mapState("user/detail", ["loadingClientProfile"]),
    ...mapState("user/verify", ["loading"]),
    ...mapState("user/list", [
      "users",
      "pagination",
      "loadingUsers",
      "statistics",
      "loadingUserStatistics",
      "affiliates",
      "tags",
      "profiles",
    ]),
    isLoading() {
      return this[this.action.type].loading;
    },
    isSubmitting() {
      return this.loading || this.loadingClientProfile;
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  watch: {
    users(val) {
      const { total, limit, page } = this.pagination;
      this.metadata = {
        from: limit * (page - 1) + 1,
        to: Math.min(limit * page, total),
        of: total,
      };
    },
    affiliates(val) {
      this.bdmOptions = [
        { label: "All", value: null },
        ...val.map((affiliate) => ({
          label: affiliate.name,
          value: affiliate.code,
        })),
      ];
    },
    tags(val) {
      this.tagOptions = [
        { label: "All", value: null },
        ...val.map((tag) => ({
          label: tag.tagName,
          value: tag.tagId,
        })),
      ];
    },
    profiles(val) {
      this.profileOptions = [
        { label: "All", value: null },
        ...val.map((profile) => ({
          label: profile.profileTypeId,
          value: profile.profileTypeId,
        })),
      ];
    },
    search() {
      this.handleSearchDebounced();
    },
    currentPage() {
      this.updateTableKey();
    },
    perPage() {
      this.updateTableKey();
    },
  },
  async created() {
    this.asyncUserStatistics();
    const calls = [this.asyncGetTags(), this.getProfileList()];
    if (this.permissions?.full_access === "admin") {
      calls.push(this.asyncGetAffiliates());
    }
    await Promise.all(calls);
  },
  methods: {
    ...mapActions({
      asyncUsers: "user/list/getUserList",
      asyncUserStatistics: "user/list/getUserStatistics",
      asyncGetAffiliates: "user/list/getAffiliates",
      asyncGetTags: "user/list/getTags",
      asyncBlockUser: "user/block/blockUser",
      asyncVerifyUser: "user/verify/verifyUser",
      asyncReopenUser: "user/reopenAccount/reopenUser",
      asyncDeleteUser: "user/remove/deleteUser",
      asyncVerifyAccountUser: "user/verifyAccount/verifyAccountUser",
      updateClientProfile: "user/detail/updateClientProfile",
      getProfileList: "user/list/getProfileList",
      exportUserList: "user/list/exportUserList",
    }),
    formatCurrencyBuiltInFunc,
    format,
    formatPercent(value) {
      if (value === null || value === undefined) return "-";
      const n = Number(value);
      if (isNaN(n)) return "-";
      // If value looks like a fraction (<1) treat as fraction to percentage
      if (Math.abs(n) < 1) return `${(n * 100).toFixed(2)}%`;
      return `${n.toFixed(2)}%`;
    },
    formatDobState(item) {
      const dob = item.dob;
      const state = item.state || "-";
      const dateStr = dob ? format(new Date(dob), "dd/MM/yyyy") : "-";
      const combined = `${dateStr} ${state}`.trim();
      return combined.length > 39 ? combined.slice(0, 39) : combined;
    },
    getBreakType(item) {
      try {
        const start = parseISO(item.breakStartDate);
        const end = parseISO(item.breakTime);
        const days = differenceInDays(end, start);
        if (days >= 180) return `Long Break`;
        return `Short Break`;
      } catch (e) {
        return "On Break";
      }
    },
    async fetchUsers() {
      if (this.loadingUsers) return;

      const {
        search,
        perPage: limit,
        currentPage: page,
        profileOrderValue,
        sortOrderValue: sortValue,
        sortFieldValue: sortField,
        roleFilter: roleId,
        registerDate,
        lastLoggedDate,
        bdmOrderValue,
        tagOrderValue,
        statusOrderValue,
      } = this;
      const statusFieldMap = {
        Active: { isActive: 1 },
        Inactive: { isActive: 0 },
        UnVerified: { isVerified: 0 },
        Blocked: { isBlocked: 1 },
        "Bet Stopped": { isBetStopFound: 1 },
        Closed: { selfClosed: 1 },
        Break: { isInBreakTime: 1 },
        "Self Excluded": { selfExcluded: 1 },
        Banned: { isBanned: 1 },
      };

      let params = {
        search,
        limit,
        page,
        profile: profileOrderValue.value,
        sortValue,
        sortField,
        roleId,
        primaryAffiliate: bdmOrderValue.value,
        tagId: tagOrderValue.value,
      };
      if (statusOrderValue.value) {
        if (statusOrderValue.value === "Active") {
          params = {
            ...params,
            isActive: 1,
            isVerified: 1,
            isBlocked: 0,
            isBetStopFound: 0,
            selfClosed: 0,
            selfExcluded: 0,
            isBanned: 0,
          };
        } else
          params = { ...params, ...statusFieldMap[statusOrderValue.value] };
      }
      if (registerDate) {
        const [startDate, endDate] = registerDate.split(" to ");
        params["dateRange[startDate]"] = startOfDay(
          new Date(startDate),
        ).toISOString();
        params["dateRange[endDate]"] = endOfDay(
          new Date(endDate),
        ).toISOString();
      }

      if (lastLoggedDate) {
        const [startDate, endDate] = lastLoggedDate.split(" to ");
        params["lastLoginDate[startDate]"] = startOfDay(
          new Date(startDate),
        ).toISOString();
        params["lastLoginDate[endDate]"] = endOfDay(
          new Date(endDate),
        ).toISOString();
      }
      this.exportParams = { ...params };
      try {
        const data = await this.asyncUsers(params);
        return data.map((user) => ({
          ...user,
          depositLimits:
            user.depositLimits?.sort((a, b) => a.period - b.period) || [],
        }));
      } catch (error) {
        return [];
      }
    },
    setModal(title, content) {
      this.modal.title = title;
      this.modal.content = content;
    },
    showModal(type) {
      this.action = { type };
      if (type === UserAction.DELETE) this.$refs.modal.show();
    },

    async handleSubmitModal(event) {
      event.preventDefault();
    },

    async deleteUser(id) {
      const result = await this.updateClientProfile({
        userId: id,
        isActive: 0,
      });

      if (!result) return;
      this.refUserListTable.refresh();
    },
    clearFilter() {
      this.registerDate = "";
      this.lastLoggedDate = "";
      this.bdmOrderValue = this.bdmOptions[0];
      this.tagOrderValue = this.tagOptions[0];
      this.profileOrderValue = this.profileOptions[0];
      this.statusOrderValue = this.statusOptions[0];
      this.updateTableKey();
    },
    updateTableKey() {
      this.tableKey++;
    },

    handleSearch() {
      this.currentPage = 1;
      this.updateTableKey();
    },
    filter() {
      this.updateTableKey();
    },
    formatPeriod(period) {
      switch (period) {
        case 1:
          return "Day";
        case 7:
          return "Week";
        case 14:
          return "2 Weeks";
        case 30:
          return "Month";
        default:
          return "Day";
      }
    },
    async handleExportData() {
      if (this.exportData.length > 0) {
        return;
      }
      const data = await this.exportUserList(this.exportParams);
      const list = data.map((user) => ({
        Name: `${user.firstName} ${user.middleName} ${user.lastName}`,
        Username: user.username,
        "Account No.": user.userId,
        Email: user.email,
        Mobile: user.phoneNumber,
        BDM: user.primaryAffiliateName || "-",
        Profile: user.profile || "-",
        Balance: this.formatCurrencyBuiltInFunc(user.balance),
        "Date of Rego": this.formatDateTimeAustralia(user.createdTime),
        "Total GGR": this.formatCurrencyBuiltInFunc(user.totalGgr),
        "GGR%": this.formatCurrencyBuiltInFunc(user.ggrPercentage),
        Limits: user.depositLimits.length
          ? user.depositLimits
              ?.sort((a, b) => a.period - b.period)
              .map(
                (limit) =>
                  `${this.formatCurrencyBuiltInFunc(
                    limit.amount,
                  )}/${this.formatPeriod(limit.period)}`,
              )
              .join(", ")
          : "-",
        Status: this.formatStatus(user),
        "Last Bet": user.lastBetslipCreatedTime
          ? this.formatDateTimeAustralia(user.lastBetslipCreatedTime)
          : "-",
      }));
      this.exportData = list;
    },
    resetExportData() {
      this.exportData = [];
    },
    formatStatus(item) {
      const {
        isActive,
        isVerified,
        isBlocked,
        isBanned,
        selfExcluded,
        selfClosed,
        breakStartDate,
        breakTime,
      } = item;

      if (!isActive) return "Inactive";
      if (!isVerified) return "UnVerified";
      if (isBlocked) return "Blocked";
      if (selfClosed) return "Closed";
      if (selfExcluded) return "Self Excluded";
      if (isBanned) return "Banned";
      if (breakStartDate && breakTime) {
        if (!breakStartDate || !breakTime) return "";
        if (new Date(breakTime) < new Date() && isActive) return "Active";
        let breakDuration = "On Break";
        const start = parseISO(breakStartDate);
        const end = parseISO(breakTime);
        const days = differenceInDays(end, start);
        if (days >= 1) return (breakDuration += ` ${days}d`);
        const hours = differenceInHours(end, start);
        if (hours >= 1) return (breakDuration += ` ${hours}h`);
        const minutes = differenceInMinutes(end, start);
        return (breakDuration += ` ${minutes}m`);
      }
      return "Active";
    },
    variantStatus(item) {
      const {
        isActive,
        isVerified,
        isBlocked,
        isBanned,
        selfExcluded,
        selfClosed,
        breakStartDate,
        breakTime,
      } = item;

      if (!isActive) return "danger";
      if (!isVerified) return "primary";
      if (isBlocked) return "warning";
      if (selfClosed) return "secondary";
      if (selfExcluded) return "dark";
      if (isBanned) return "danger";
      if (breakStartDate && breakTime && new Date(breakTime) > new Date())
        return "info";
      return "success";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
.per-page-selector {
  width: 90px;
}
.pointer {
  cursor: pointer;
}
</style>
